// BodyClipLoader — streams the non-priority body clips into the live mixer after
// the avatar is ready, and lets the render loop pull a specific clip to the
// front of the queue on demand.
//
// At character load AvatarModel registers a small priority set (idle/breathing/
// fidget — see priorityClips.js) before flipping __AVATAR_READY__, then hands
// the REST of the clips to a loader created here. The loader drains a few at a
// time during idle frames (requestIdleCallback). When the graph requests a clip
// that isn't loaded yet — an SM transition to listening/thinking/talking, or a
// gesture — GraphEvaluator / GestureChannel call request(name) to jump it to the
// front so it's available within a tick or two instead of waiting out the queue.
//
// All state is plain Maps/Sets/arrays — no React state, no per-frame allocation.
// request() in steady state is a Map.has + early return (it fires at most once
// per clip, before that clip enters the loading set).

/**
 * @param {object} opts
 * @param {Array<{name:string}>} opts.entries  remaining (non-priority) clip entries to load.
 * @param {(entry) => Promise<any>} opts.loadAndRegister  loads one entry's GLB and registers it on the mixer.
 * @param {(cb: Function) => void} opts.schedule  schedules a drain tick (requestIdleCallback / setTimeout).
 * @param {number} [opts.batchSize]  clips to kick off per drain tick (default 2).
 * @param {number} [opts.maxConcurrent]  cap on simultaneous in-flight background
 *   loads (default Infinity). Bounds bandwidth so the background sweep doesn't
 *   starve an on-demand pull (which bypasses the cap). A character with dozens of
 *   clips otherwise fires dozens of parallel fetches that split the pipe and make
 *   the one clip a user is actually waiting on load slowly.
 */
export function createBodyClipLoader({ entries, loadAndRegister, schedule, batchSize = 2, maxConcurrent = Infinity }) {
  const pending = new Map()        // name -> entry (not yet started)
  const queue = []                 // ordered list of pending names (front = next)
  const loading = new Set()        // names whose load is in flight
  const loaded = new Set()         // names fully registered
  const waiters = new Map()        // name -> [resolve, …] for ensure()
  let started = false
  let draining = false
  let paused = false               // background sweep held; on-demand pulls unaffected

  for (const e of entries ?? []) {
    if (!e?.name || pending.has(e.name)) continue
    pending.set(e.name, e)
    queue.push(e.name)
  }

  function beginLoad(name) {
    const entry = pending.get(name)
    if (!entry) return
    pending.delete(name)
    loading.add(name)
    let p
    try { p = loadAndRegister(entry) } catch (err) { p = Promise.reject(err) }
    Promise.resolve(p)
      .catch(() => { /* loadAndRegister already warns; never reject the drain */ })
      .finally(() => {
        loading.delete(name)
        loaded.add(name)
        const ws = waiters.get(name)
        if (ws) { waiters.delete(name); for (const r of ws) r() }
        // A background slot freed — keep draining if the cap was the limiter.
        // Not while paused: an on-demand completion must not restart the sweep.
        if (started && !paused && queue.length > 0) scheduleDrain()
      })
  }

  // Pull a clip to the front of the queue and start it now. No-op if already
  // loading / loaded / not ours. Cheap + idempotent — safe to call every frame.
  function request(name) {
    if (!pending.has(name)) return // loading / loaded / not ours
    const i = queue.indexOf(name)
    if (i > 0) { queue.splice(i, 1); queue.unshift(name) }
    beginLoad(name)
  }

  function drain() {
    draining = false
    if (paused) return // a tick scheduled before pause() lands here — drop it
    let kicked = 0
    while (queue.length > 0 && kicked < batchSize && loading.size < maxConcurrent) {
      const name = queue.shift()
      if (!pending.has(name)) continue // already pulled on demand
      beginLoad(name)
      kicked++
    }
    // Reschedule only if more work remains AND we still have capacity (this tick
    // hit batchSize). If we stopped because the concurrency cap is full, the
    // beginLoad completion handler resumes draining when a slot frees — avoids an
    // idle busy-loop hammering requestIdleCallback while every slot is in use.
    if (queue.length > 0 && loading.size < maxConcurrent) scheduleDrain()
  }

  function scheduleDrain() {
    if (draining) return
    draining = true
    schedule(drain)
  }

  return {
    /** Begin background draining. Idempotent. */
    start() {
      if (started) return
      started = true
      if (queue.length > 0 && !paused) scheduleDrain()
    },

    /**
     * Hold the BACKGROUND sweep: no new background loads start; loads already
     * in flight complete normally. On-demand pulls (request / ensure) are
     * deliberately unaffected — the graph asking for a clip still gets it.
     * Exists for the e2e fixtures (settleForUi): a UI spec needs the GLB
     * parse work to stop competing for the main thread, not the clips loaded.
     * Idempotent.
     */
    pause() { paused = true },

    /** Resume background draining after pause(). Idempotent. */
    resume() {
      if (!paused) return
      paused = false
      if (started && queue.length > 0) scheduleDrain()
    },

    /** Count of loads currently in flight (excludes pending). The observable
     *  settleForUi waits on after pause() — zero means the main thread is no
     *  longer being stalled by background GLB parses. */
    inFlight() { return loading.size },

    /**
     * Pull a clip to the front of the queue and start it immediately. No-op if
     * the clip is already loading, loaded, or unknown to this loader. Cheap and
     * idempotent — safe to call every frame from the render loop.
     */
    request,

    /**
     * Request a clip and resolve once it's registered (or already is). Resolves
     * `false` for a clip this loader doesn't own. For one-shot triggers (prefab
     * playBodyClip) that need the action present before they can act.
     */
    ensure(name) {
      if (loaded.has(name)) return Promise.resolve(true)
      if (!pending.has(name) && !loading.has(name)) return Promise.resolve(false)
      request(name)
      return new Promise(resolve => {
        const arr = waiters.get(name) ?? []
        arr.push(() => resolve(true))
        waiters.set(name, arr)
      })
    },

    isPending(name) { return pending.has(name) },
    has(name) { return loaded.has(name) },
    /** True if this loader owns the clip in any state (queued, in flight, done). */
    isManaged(name) { return pending.has(name) || loading.has(name) || loaded.has(name) },
    /** Count of clips not yet fully registered (pending + in flight). */
    remaining() { return pending.size + loading.size },
  }
}
